import { useCallback, useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { X } from 'lucide-react'
import type { ImageElement, ImageFilters } from '../../domain/canvas/entities/element'
import {
  DEFAULT_FILTERS,
  FILTER_PRESETS,
  FilterPreset,
} from '../../domain/canvas/constants/filterPresets'
import { DesignStudioFilters } from '@nicelittlethings/types'

type ImageEditPanelProps = {
  element: ImageElement
  onUpdate: (updates: Partial<ImageElement>) => void
  onClose: () => void
}

type TabType = 'basic' | 'advanced'

export const ImageEditPanel = ({ element, onUpdate, onClose }: ImageEditPanelProps) => {
  const intl = useIntlayer('imageEditPanel')

  // Local state for preview (not committed until Apply)
  const [borderRadius, setBorderRadius] = useState(element.borderRadius || 0)
  const [filters, setFilters] = useState<ImageFilters>(element.filters || DEFAULT_FILTERS)
  const [activeTab, setActiveTab] = useState<TabType>('basic')

  // Calculate max border radius (half of smallest dimension for perfect circle)
  const maxBorderRadius =
    Math.min(element.dimensions?.width || 50, element.dimensions?.height || 50) / 2

  const handleFilterChange = useCallback((key: keyof ImageFilters, value: number) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const handlePresetClick = useCallback((preset: FilterPreset) => {
    setFilters(FILTER_PRESETS[preset])
    if (preset === FilterPreset.NONE) {
      setBorderRadius(0)
    }
  }, [])

  const handleReset = useCallback(() => {
    setBorderRadius(0)
    setFilters(DEFAULT_FILTERS)
  }, [])

  const handleApply = useCallback(() => {
    onUpdate({
      borderRadius,
      filters,
    })
    onClose()
  }, [borderRadius, filters, onUpdate, onClose])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  // Generate filter SVG for preview
  const filterSVG = DesignStudioFilters.hasActiveFilters(filters)
    ? DesignStudioFilters.generateSVGFilter('preview', filters)
    : ''

  const rx =
    borderRadius > 0 ? DesignStudioFilters.calculateBorderRadius(borderRadius, 150, 150) : 0

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 md:items-center">
      <div className="w-full max-w-md bg-white rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{intl.title}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4 flex justify-center bg-gray-100">
          <svg width="150" height="150" viewBox="0 0 150 150">
            <defs>
              {borderRadius > 0 && (
                <clipPath id="preview-clip">
                  <rect x="0" y="0" width="150" height="150" rx={rx} ry={rx} />
                </clipPath>
              )}
              {filterSVG && <g dangerouslySetInnerHTML={{ __html: filterSVG }} />}
            </defs>
            <image
              href={element.src}
              width="150"
              height="150"
              preserveAspectRatio="xMidYMid slice"
              clipPath={borderRadius > 0 ? 'url(#preview-clip)' : undefined}
              filter={
                DesignStudioFilters.hasActiveFilters(filters) ? 'url(#filter-preview)' : undefined
              }
            />
          </svg>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('basic')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'basic'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {intl.tabBasic}
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'advanced'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {intl.tabAdvanced}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="p-4 space-y-6">
          {activeTab === 'basic' && (
            <>
              {/* Border Radius */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.borderRadius}</label>
                  <span className="text-sm text-gray-500">{borderRadius.toFixed(1)}mm</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={maxBorderRadius}
                  step="0.5"
                  value={borderRadius}
                  onChange={(e) => setBorderRadius(parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{intl.square}</span>
                  <span>{intl.circle}</span>
                </div>
              </div>

              {/* Presets */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-3">
                  {intl.presets}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => handlePresetClick(FilterPreset.NONE)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetNone}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.BW)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetBw}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.SEPIA)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetSepia}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.VINTAGE)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetVintage}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.VIBRANT)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetVibrant}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.COOL)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetCool}
                  </button>
                  <button
                    onClick={() => handlePresetClick(FilterPreset.WARM)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {intl.presetWarm}
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'advanced' && (
            <>
              {/* Brightness */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.brightness}</label>
                  <span className="text-sm text-gray-500">{filters.brightness}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="1"
                  value={filters.brightness}
                  onChange={(e) => handleFilterChange('brightness', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Contrast */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.contrast}</label>
                  <span className="text-sm text-gray-500">{filters.contrast}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="1"
                  value={filters.contrast}
                  onChange={(e) => handleFilterChange('contrast', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Saturation */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.saturation}</label>
                  <span className="text-sm text-gray-500">{filters.saturation}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="1"
                  value={filters.saturation}
                  onChange={(e) => handleFilterChange('saturation', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Blur */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.blur}</label>
                  <span className="text-sm text-gray-500">{filters.blur.toFixed(1)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={filters.blur}
                  onChange={(e) => handleFilterChange('blur', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Sepia */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.sepia}</label>
                  <span className="text-sm text-gray-500">{filters.sepia}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={filters.sepia}
                  onChange={(e) => handleFilterChange('sepia', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Hue Rotate */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">{intl.hue}</label>
                  <span className="text-sm text-gray-500">{filters.hueRotate}°</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="1"
                  value={filters.hueRotate}
                  onChange={(e) => handleFilterChange('hueRotate', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-2">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            {intl.reset}
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            {intl.cancel}
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            {intl.apply}
          </button>
        </div>
      </div>
    </div>
  )
}
